import { type RefObject, Suspense, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { BoxHelper } from 'three';
import { useShallow } from 'zustand/shallow';

import { ArticlePreviewPlaceholder } from 'app/modules/wallforming/components/catalogue-container/article-preview/ArticlePreviewPlaceholder';
import { ArticlePreviewMesh } from 'app/modules/wallforming/components/scenes/objects/articles/ArticlePreviewMesh';
import { GLTFLoader } from 'app/modules/wallforming/components/scenes/objects/helpers/GLTFLoader';
import { RepresentationLoader } from 'app/modules/wallforming/components/scenes/objects/helpers/RepresentationLoader';
import { PrefabLayerPreviewRenderer } from 'app/modules/wallforming/components/scenes/prefab-layer-renderer/PrefabLayerPreviewRenderer';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { ErrorBoundaryFallback } from 'shared/components/error-handling/ErrorBoundaryFallback';
import { LoadingAnimation } from 'shared/components/LoadingAnimation';

import type { CameraControls } from '@react-three/drei';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';

interface ArticlePreviewSceneProps {
    previewCameraControlsRef: RefObject<CameraControls | null>;
    previewHelperRef: RefObject<BoxHelper | undefined>;
    restorePreviewCamera: () => boolean;
}

export const ArticlePreviewScene = ({
    previewCameraControlsRef,
    previewHelperRef,
    restorePreviewCamera
}: ArticlePreviewSceneProps) => {
    const { t } = useTranslation();
    const selectedArticleId = usePlacementStore(useShallow((state) => state.selectedArticleId));
    const loadingFailed = useRef<boolean>(false);

    const handlePreviewCameraControls = useCallback(() => {
        if (previewCameraControlsRef.current) {
            previewCameraControlsRef.current.enabled = selectedArticleId !== null && !loadingFailed.current;
        }
    }, [previewCameraControlsRef, selectedArticleId]);

    useEffect(() => {
        handlePreviewCameraControls();
    }, [handlePreviewCameraControls]);

    useEffect(() => {
        const subscribedSelectedArticleId = usePlacementStore.subscribe(
            (state) => state.selectedArticleId,
            () => {
                if (previewCameraControlsRef.current) {
                    previewCameraControlsRef.current.setTarget(0, 0, 0, false);
                    previewCameraControlsRef.current.camera.updateProjectionMatrix();
                }

                loadingFailed.current = false;
            }
        );

        return () => subscribedSelectedArticleId();
    }, [previewCameraControlsRef]);

    if (!selectedArticleId) {
        return <ArticlePreviewPlaceholder />;
    }

    return (
        <ErrorBoundary
            key={selectedArticleId}
            fallbackRender={({ error }) => (
                <ErrorBoundaryFallback description={t('errorLoadingArticle')} error={error} withinCanvas={true} />
            )}
            onError={() => {
                loadingFailed.current = true;
                handlePreviewCameraControls();
            }}
        >
            <Suspense fallback={<LoadingAnimation text={t('labelLoadArticle')} />}>
                <RepresentationLoader articleId={selectedArticleId} isInteractive={true}>
                    {({ articleRepresentation, gltfAssets, isInteractive }) => (
                        <GLTFLoader
                            articleId={selectedArticleId}
                            articleRepresentation={articleRepresentation}
                            gltfAssets={gltfAssets}
                            isMainCanvas={isInteractive}
                        >
                            {({ articleMeshes, articleRepresentation }) =>
                                selectedArticleId && (
                                    <>
                                        <ArticlePreviewMesh
                                            key={selectedArticleId}
                                            articleId={selectedArticleId}
                                            articleMeshes={articleMeshes}
                                            articleRepresentation={articleRepresentation}
                                            previewCameraControlsRef={previewCameraControlsRef}
                                            previewHelperRef={previewHelperRef}
                                            restorePreviewCamera={restorePreviewCamera}
                                        />
                                        <axesHelper args={[5]} />

                                        {IS_DEV_TOOLS && <PrefabLayerPreviewRenderer articleId={selectedArticleId} />}
                                    </>
                                )
                            }
                        </GLTFLoader>
                    )}
                </RepresentationLoader>
            </Suspense>
        </ErrorBoundary>
    );
};
